'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { LayoutGroup, motion } from 'motion/react';
import { type ForwardedRef, forwardRef, type ReactNode, useRef } from 'react';

// --- Types ---

export interface OptionTabGroup<T> {
  value: T;
  label: string;
  isDisabled?: boolean;
  tooltip?: ReactNode;
  count?: number;
  /**
   * Visual treatment for the count badge.
   * - `muted` (default): informational grey pill.
   * - `notification`: attention-drawing filled badge for actionable items.
   */
  countTone?: 'muted' | 'notification';
}
export interface TabGroupProps<T> {
  id: string;
  options: OptionTabGroup<T>[];
  value?: string;
  onChange?: (value: T) => void;
  hasActiveBorder?: boolean;
  mobileVariant?: 'default' | 'expanded';
  rightElement?: ReactNode;
  className?: string;
}

// --- Component ---

function TabGroupInner(t0, ref) {
  const $ = _c(30);
  let className;
  let hasActiveBorder;
  let id;
  let onChange;
  let options;
  let props;
  let rightElement;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      options,
      id,
      value,
      onChange,
      hasActiveBorder,
      mobileVariant: t1,
      rightElement,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = hasActiveBorder;
    $[3] = id;
    $[4] = onChange;
    $[5] = options;
    $[6] = props;
    $[7] = rightElement;
    $[8] = t1;
    $[9] = value;
  } else {
    className = $[1];
    hasActiveBorder = $[2];
    id = $[3];
    onChange = $[4];
    options = $[5];
    props = $[6];
    rightElement = $[7];
    t1 = $[8];
    value = $[9];
  }
  const mobileVariant = t1 === undefined ? "default" : t1;
  const triggerRefList = useRef({});
  const handleTabClick = optionValue => {
    onChange?.(optionValue);
    const tabElement = triggerRefList.current[String(optionValue)];
    if (tabElement) {
      tabElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
    }
  };
  let t2;
  if ($[10] !== className) {
    t2 = cn("relative grid w-full", className);
    $[10] = className;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = "scrollbar-hidden h-16 w-full overflow-x-auto";
  const T0 = LayoutGroup;
  const t4 = mobileVariant === "expanded" && "max-sm:justify-between max-sm:items-center";
  let t5;
  if ($[12] !== t4) {
    t5 = cn("gap-sm sm:gap-md flex h-full w-full", t4);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = options.map(option => {
    const isActive = value !== undefined && option.value === value;
    const isDisabled = option.isDisabled === true;
    return <Tooltip key={id + option.value}><TooltipTrigger asChild={true}><button ref={el => {
          triggerRefList.current[String(option.value)] = el;
        }} type="button" onClick={() => !isDisabled && handleTabClick(option.value)} data-testid={`tab-${option.value}`} aria-disabled={isDisabled || undefined} className={cn("relative flex h-full items-center border-b border-transparent whitespace-nowrap", "gap-2xs transition-colors duration-150 ease-linear", "text-3xs sm:text-sm", mobileVariant === "expanded" && "max-sm:flex-1 max-sm:min-w-0 max-sm:justify-center max-sm:text-xs", isActive ? "text-primary" : isDisabled ? "text-muted-foreground cursor-not-allowed" : "text-secondary-foreground cursor-pointer", !isDisabled && !isActive && "hover:text-foreground")}>{option.label}{option.count !== undefined && option.count > 0 && <span className={cn("ml-2xs h-[18px] min-w-[18px] px-[6px]", "text-[11px] leading-none", "flex items-center justify-center", "rounded-full", option.countTone === "notification" ? "bg-info-foreground text-white" : "bg-muted")}>{option.count}</span>}{isActive && hasActiveBorder && <motion.div layoutId="active-tab-border" className="border-ring absolute bottom-[-1px] left-0 w-full border-b-[0.5px]" transition={{
            type: "spring",
            bounce: 0,
            duration: 0.35
          }} style={{
            originY: "0px"
          }} />}</button></TooltipTrigger>{option.tooltip && <TooltipContent>{option.tooltip}</TooltipContent>}</Tooltip>;
  });
  let t7;
  if ($[14] !== rightElement) {
    t7 = rightElement && <div className="ml-auto justify-center self-center">{rightElement}</div>;
    $[14] = rightElement;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== ref || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div ref={ref} className={t5}>{t6}{t7}</div>;
    $[16] = ref;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== T0 || $[22] !== id || $[23] !== t8) {
    t9 = <div className={t3}><T0 id={id}>{t8}</T0></div>;
    $[21] = T0;
    $[22] = id;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="border-border relative -top-px z-1 border-b-[0.5px]" style={{
      maskImage: "linear-gradient(to right, black 92.5%, transparent)",
      WebkitMaskImage: "linear-gradient(to right, black 92.5%, transparent)"
    }} />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== props || $[27] !== t2 || $[28] !== t9) {
    t11 = <div className={t2} {...props}>{t9}{t10}</div>;
    $[26] = props;
    $[27] = t2;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}

// Generic forwardRef
// oxlint-disable-next-line typescript/consistent-type-assertions
export const TabGroup = forwardRef(TabGroupInner) as <T>(props: TabGroupProps<T> & {
  ref?: ForwardedRef<HTMLDivElement>;
}) => ReturnType<typeof TabGroupInner>;